import { TransmissionSystemOperatorResource } from 'app/_resources/transmission-system-operator.resource';
import { AuthService } from 'app/_services/auth.service';
import { Growl } from 'angular/custom/services/growl.service';
import { ConfirmModal } from 'app/_services/confirm-modal.factory';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import * as angular from 'angular';
import { Decimal } from 'decimal.js';
import { TogglePanelCallback } from 'angular/custom/components/toggle-panel.component';

class OperatorSettingsAuctionsCtrl implements ng.IController {
    public settings: TsoAuctionSettingsDto;
    public loading: boolean = true;
    public allowCapacityUpgrade: boolean;
    private authenticatedUser: LoginResult;
    public enableAuctionNote: boolean;
    public loadingErrorMsg?: string;
    public volBasedYearly: boolean;
    public volBasedQuarterly: boolean;
    public volBasedMonthly: boolean;
    public volBasedDaily: boolean;
    private numberOfNetworkPointsWithEnabledVolumeBasedTariff: number;
    public balancingGroupPortfolioCodeSelection: BalancingGroupPortfolioCodeSelection;

    constructor(
        private TransmissionSystemOperator: TransmissionSystemOperatorResource,
        private authService: AuthService,
        private growl: Growl,
        private confirmModal: ConfirmModal,
        private errorHandler: ErrorHandler,
        private $q: ng.IQService
    ) {}

    $onInit(): void {
        this.authService.userLoginPromise.promise.then(
            (loginResult: LoginResult) => {
                this.authenticatedUser = loginResult;
                this.getAuctionSettings();

                this.TransmissionSystemOperator.getNumberOfNetworkPointsWithEnabledVolumeBasedTariff(
                    { id: this.authenticatedUser.referenceId },
                    (res: number) =>
                        (this.numberOfNetworkPointsWithEnabledVolumeBasedTariff =
                            res)
                );
            }
        );
    }

    private getAuctionSettings() {
        this.TransmissionSystemOperator.getAuctionSettings(
            { id: this.authenticatedUser.referenceId },
            (res: TsoAuctionSettingsDto) => {
                this.settings = res;
                this.loading = false;
                this.enableAuctionNote =
                    this.settings.auctionNote &&
                    this.settings.auctionNote != '';
                this.allowCapacityUpgrade =
                    this.settings.upgradePossibleLT ||
                    this.settings.upgradePossibleST;
                this.loadingErrorMsg = null;
                this.volBasedYearly =
                    this.settings.volumeBasedTariffEnabled &&
                    this.settings.volumeBasedTariffFactorForYearly != null;
                this.volBasedQuarterly =
                    this.settings.volumeBasedTariffEnabled &&
                    this.settings.volumeBasedTariffFactorForQuarterly != null;
                this.volBasedMonthly =
                    this.settings.volumeBasedTariffEnabled &&
                    this.settings.volumeBasedTariffFactorForMonthly != null;
                this.volBasedDaily =
                    this.settings.volumeBasedTariffEnabled &&
                    this.settings.volumeBasedTariffFactorForDaily != null;
            },
            (err: any) => {
                this.loadingErrorMsg =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'Something went wrong when loading the auction settings. Please check back later.';
            }
        ).$promise.finally(() => (this.loading = false));
    }

    public deactivateAuctionNote(cb: TogglePanelCallback) {
        this.settings.auctionNote = '';
        return this.TransmissionSystemOperator.saveAuctionNote(
            { id: this.authenticatedUser.referenceId },
            this.settings.auctionNote,
            () => cb(true),
            () => cb(false)
        );
    }

    public toggleCapacityUpgrade(cb: TogglePanelCallback) {
        this.TransmissionSystemOperator.saveUpgradePossibleForShortTerm(
            { id: this.authenticatedUser.referenceId },
            this.allowCapacityUpgrade,
            (res) => {
                cb(true);
                this.settings.upgradePossibleST = this.allowCapacityUpgrade;
                this.settings.upgradePossibleLT = this.allowCapacityUpgrade;
                this.saveEnableUpgradeForLongTerm(
                    this.allowCapacityUpgrade,
                    true
                );
                this.growl.show('Successfully saved.', 'success');
            },
            (err) => {
                cb(false);
                this.errorHandler(err);
            }
        );
    }

    public toggleReverseAuctionConfiguration(cb: TogglePanelCallback) {
        this.TransmissionSystemOperator.saveReverseAuctionConfigurationSupported(
            { id: this.authenticatedUser.referenceId },
            this.settings.reverseAuctionConfigurationSupported,
            () => {
                cb(true);
                this.growl.show('Successfully saved.', 'success');
                this.resetReverseAuctionConfigurations();
            },
            (err) => {
                cb(false);
                this.errorHandler(err);
            }
        );
    }

    public saveContractIdOnUpgradeSelection(value, silent) {
        this.TransmissionSystemOperator.saveContractIdOnUpgradeSelection(
            { id: this.authenticatedUser.referenceId },
            { value: value },
            (res: any) => {
                if (!silent) {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                }
            },
            this.errorHandler
        );
    }

    public saveEnableUpgradeForShortTerm(value, silent) {
        this.TransmissionSystemOperator.saveUpgradePossibleForShortTerm(
            { id: this.authenticatedUser.referenceId },
            value,
            (res: any) => {
                if (!silent) {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                }
            },
            this.errorHandler
        );
    }

    public saveEnableUpgradeForLongTerm(value, silent) {
        this.TransmissionSystemOperator.saveUpgradePossibleForLongTerm(
            { id: this.authenticatedUser.referenceId },
            value,
            (res: any) => {
                if (!silent) {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                }
            },
            this.errorHandler
        );
    }

    public toggleUnbundledToBundledConversion(cb: TogglePanelCallback) {
        this.TransmissionSystemOperator.toggleUnbundledToBundledConversion(
            { id: this.authenticatedUser.referenceId },
            this.settings.allowConversion,
            () => {
                this.settings.allowConversionForYear = false;
                this.settings.allowConversionForQuarter = false;
                this.settings.allowConversionForMonth = false;
                this.settings.allowConversionForDayAhead = false;
                this.settings.limitBidMandatoryForConversion = false;
                this.settings.multipleConversionsPerBid = false;
                cb(true);
            },
            () => cb(false)
        );
    }

    public toggleUnbundledToBundledConversionContractIdSelectionPossibility(): void {
        this.TransmissionSystemOperator.toggleUnbundledToBundledConversionContractIdSelectionPossibility(
            { id: this.authenticatedUser.referenceId },
            {
                value: this.settings
                    .unbundledToBundledConversionContractIdSelection
            },
            () => {
                this.growl.show('Successfully saved.', 'success', {
                    delay: 2000
                });
            },
            this.errorHandler
        );
    }

    public saveConversionYearlySelection(value, silent) {
        this.TransmissionSystemOperator.saveConversionYearlySelection(
            { id: this.authenticatedUser.referenceId },
            value,
            (res: any) => {
                if (!silent) {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                }
            },
            this.errorHandler
        );
    }

    public saveConversionQuarterlySelection(value, silent) {
        this.TransmissionSystemOperator.saveConversionQuarterlySelection(
            { id: this.authenticatedUser.referenceId },
            value,
            (res: any) => {
                if (!silent) {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                }
            },
            this.errorHandler
        );
    }

    public saveConversionMonthlySelection(value, silent) {
        this.TransmissionSystemOperator.saveConversionMonthlySelection(
            { id: this.authenticatedUser.referenceId },
            value,
            (res: any) => {
                if (!silent) {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                }
            },
            this.errorHandler
        );
    }

    public allowConversionEnterRuntimeToggle() {
        this.TransmissionSystemOperator.saveAllowRuntimeForConversion(
            { id: this.authenticatedUser.referenceId },
            this.settings.runtimeSelectionPossibleForConversion,
            (res: any) => {
                this.growl.show('Successfully saved.', 'success', {
                    delay: 2000
                });
            },
            this.errorHandler
        );
    }

    public mandateConversionLimitBidToggle() {
        // TODO EM-1088: Add sum validation
        this.TransmissionSystemOperator.saveMandateLimitBidForConversion(
            { id: this.authenticatedUser.referenceId },
            this.settings.limitBidMandatoryForConversion,
            (res: any) => {
                this.growl.show('Successfully saved.', 'success', {
                    delay: 2000
                });
            },
            this.errorHandler
        );
    }

    public multipleConversionsPerBidToggle() {
        this.TransmissionSystemOperator.saveMultipleConversionsPerBid(
            { id: this.authenticatedUser.referenceId },
            this.settings.multipleConversionsPerBid,
            (res: any) => {
                this.growl.show('Successfully saved.', 'success', {
                    delay: 2000
                });
            },
            this.errorHandler
        );
    }

    public saveConversionDayAheadSelection(value, silent) {
        this.TransmissionSystemOperator.saveConversionDayAheadSelection(
            { id: this.authenticatedUser.referenceId },
            value,
            (res: any) => {
                if (!silent) {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                }
            },
            this.errorHandler
        );
    }

    public saveAuctionNote(): ng.IPromise<any> {
        return this.TransmissionSystemOperator.saveAuctionNote(
            { id: this.authenticatedUser.referenceId },
            this.settings.auctionNote
        ).$promise;
    }

    public toggleVolumeBasedTariff(cb: TogglePanelCallback) {
        this.TransmissionSystemOperator.deactivateVolumeBasedTariff(
            { id: this.authenticatedUser.referenceId },
            () => {
                delete this.settings.volumeBasedTariffFactorForDaily;
                delete this.settings.volumeBasedTariffFactorForMonthly;
                delete this.settings.volumeBasedTariffFactorForQuarterly;
                delete this.settings.volumeBasedTariffFactorForYearly;
                this.volBasedDaily = false;
                this.volBasedMonthly = false;
                this.volBasedQuarterly = false;
                this.volBasedYearly = false;
                cb(true);
            },
            () => cb(false)
        );
    }

    public toggleYearlyVolBasedTariff(
        isChangeOfFactorValue: boolean = false
    ): ng.IPromise<any> {
        if (this.volBasedYearly && !isChangeOfFactorValue) {
            this.settings.volumeBasedTariffFactorForYearly = {
                value: '1.00',
                floatingPointNumber: true
            };
        }

        if (!this.volBasedYearly && !isChangeOfFactorValue) {
            this.settings.volumeBasedTariffFactorForYearly = null;
        }

        const promise: ng.IPromise<any> =
            this.TransmissionSystemOperator.updateYearlyVolBasedTariff(
                { id: this.authenticatedUser.referenceId },
                this.settings.volumeBasedTariffFactorForYearly
            ).$promise;

        if (!isChangeOfFactorValue) {
            promise
                .then((res: any) => {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                    this.settings.volumeBasedTariffFactorForYearly = res;
                })
                .catch((err: ng.IHttpPromiseCallbackArg<RestError>) => {
                    this.errorHandler(err);
                    this.volBasedYearly = true;
                });
        }

        return promise;
    }

    public toggleQuarterlyVolBasedTariff(
        isChangeOfFactorValue: boolean = false
    ): ng.IPromise<any> {
        if (this.volBasedQuarterly && !isChangeOfFactorValue) {
            this.settings.volumeBasedTariffFactorForQuarterly = {
                value: '1.00',
                floatingPointNumber: true
            };
        }

        if (!this.volBasedQuarterly && !isChangeOfFactorValue) {
            this.settings.volumeBasedTariffFactorForQuarterly = null;
        }

        const promise: ng.IPromise<any> =
            this.TransmissionSystemOperator.updateQuarterlyVolBasedTariff(
                { id: this.authenticatedUser.referenceId },
                this.settings.volumeBasedTariffFactorForQuarterly
            ).$promise;

        if (!isChangeOfFactorValue) {
            promise
                .then((res: any) => {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                    this.settings.volumeBasedTariffFactorForQuarterly = res;
                })
                .catch((err: ng.IHttpPromiseCallbackArg<RestError>) => {
                    this.errorHandler(err);
                    this.volBasedQuarterly = true;
                });
        }

        return promise;
    }

    public toggleMonthlyVolBasedTariff(
        isChangeOfFactorValue: boolean = false
    ): ng.IPromise<any> {
        if (this.volBasedMonthly && !isChangeOfFactorValue) {
            this.settings.volumeBasedTariffFactorForMonthly = {
                value: '1.00',
                floatingPointNumber: true
            };
        }

        if (!this.volBasedMonthly && !isChangeOfFactorValue) {
            this.settings.volumeBasedTariffFactorForMonthly = null;
        }

        const promise: ng.IPromise<any> =
            this.TransmissionSystemOperator.updateMonthlyVolBasedTariff(
                { id: this.authenticatedUser.referenceId },
                this.settings.volumeBasedTariffFactorForMonthly
            ).$promise;

        if (!isChangeOfFactorValue) {
            promise
                .then((res: any) => {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                    this.settings.volumeBasedTariffFactorForMonthly = res;
                })
                .catch((err: ng.IHttpPromiseCallbackArg<RestError>) => {
                    this.errorHandler(err);
                    this.volBasedMonthly = true;
                });
        }

        return promise;
    }

    public toggleDailyVolBasedTariff(
        isChangeOfFactorValue: boolean = false
    ): ng.IPromise<any> {
        if (this.volBasedDaily && !isChangeOfFactorValue) {
            this.settings.volumeBasedTariffFactorForDaily = {
                value: '1.00',
                floatingPointNumber: true
            };
        }

        if (!this.volBasedDaily && !isChangeOfFactorValue) {
            this.settings.volumeBasedTariffFactorForDaily = null;
        }

        const promise: ng.IPromise<any> =
            this.TransmissionSystemOperator.updateDailyVolBasedTariff(
                { id: this.authenticatedUser.referenceId },
                this.settings.volumeBasedTariffFactorForDaily
            ).$promise;

        if (!isChangeOfFactorValue) {
            promise
                .then((res: any) => {
                    this.growl.show('Successfully saved.', 'success', {
                        delay: 2000
                    });
                    this.settings.volumeBasedTariffFactorForDaily = res;
                })
                .catch((err: ng.IHttpPromiseCallbackArg<RestError>) => {
                    this.errorHandler(err);
                    this.volBasedDaily = true;
                });
        }

        return promise;
    }

    public toggleAutomaticCancellationOfInterruptibleDayAhead(
        cb: TogglePanelCallback
    ): void {
        const disabling: boolean =
            !this.settings.automaticCancellationOfInterruptibleDayAhead;

        const action: () => angular.IPromise<any> = disabling
            ? this.disableAutomaticCancellationOfInterruptibleDayAhead
            : this.saveAutomaticCancellationOfInterruptibleDayAhead;

        action
            .bind(this)()
            .then(() => {
                cb(true);
                this.growl.show('Successfully saved.', 'success', {
                    delay: 2000
                });
            })
            .catch((err: ng.IHttpPromiseCallbackArg<RestError>) => {
                cb(false);
                this.errorHandler(err);
            });
    }

    public saveAutomaticCancellationOfInterruptibleDayAhead(): ng.IPromise<any> {
        if (
            !this.settings.automaticCancellationOfInterruptibleDayAheadFactor ||
            !this.settings.automaticCancellationOfInterruptibleDayAheadFactor
                .value
        ) {
            this.settings.automaticCancellationOfInterruptibleDayAheadFactor = {
                value: new Decimal(0),
                floatingPointNumber: true
            };
        }

        const valueAsDecimal: Decimal = new Decimal(
            this.settings.automaticCancellationOfInterruptibleDayAheadFactor.value
        );

        if (valueAsDecimal.greaterThan(100) || valueAsDecimal.lessThan(0)) {
            return this.$q.reject(<ng.IHttpPromiseCallbackArg<RestError>>{
                data: { message: 'Please enter a value from 0 to 100.' }
            });
        }

        return this.TransmissionSystemOperator.saveAutomaticCancellationOfInterruptibleDayAhead(
            { id: this.authenticatedUser.referenceId },
            this.settings.automaticCancellationOfInterruptibleDayAheadFactor,
            (res) =>
                (this.settings.automaticCancellationOfInterruptibleDayAheadFactor =
                    res)
        ).$promise;
    }

    public disableAutomaticCancellationOfInterruptibleDayAhead(): ng.IPromise<any> {
        return this.TransmissionSystemOperator.disableAutomaticCancellationOfInterruptibleDayAhead(
            { id: this.authenticatedUser.referenceId },
            () =>
                (this.settings.automaticCancellationOfInterruptibleDayAheadFactor =
                    undefined)
        ).$promise;
    }

    public saveContractIdSelectionForFlowCommitmentForReverseAuction(
        selection: SelectionPossibility
    ) {
        this.TransmissionSystemOperator.saveContractIdSelectionForFlowCommitmentForReverseAuction(
            { id: this.authenticatedUser.referenceId },
            { value: selection },
            (res: any) =>
                this.growl.show('Successfully saved.', 'success', {
                    delay: 2000
                }),
            this.errorHandler
        );
    }

    public saveContractIdSelectionForBuyBackForReverseAuction(
        selection: SelectionPossibility
    ) {
        this.TransmissionSystemOperator.saveContractIdSelectionForBuyBackForReverseAuction(
            { id: this.authenticatedUser.referenceId },
            { value: selection },
            (res: any) =>
                this.growl.show('Successfully saved.', 'success', {
                    delay: 2000
                }),
            this.errorHandler
        );
    }

    private resetReverseAuctionConfigurations(): void {
        this.settings.contractIdSelectionForBuyBackForReverseAuction =
            'NOT_APPLICABLE';
        this.settings.contractIdSelectionForFlowCommitmentForReverseAuction =
            'NOT_APPLICABLE';
        this.settings.balancingGroupSelectionForBuyBackForReverseAuction =
            'NOT_APPLICABLE';
        this.settings.balancingGroupSelectionForFlowCommitmentForReverseAuction =
            'NOT_APPLICABLE';
        this.settings.portfolioCodeSelectionForBuyBackForReverseAuction =
            'NOT_APPLICABLE';
        this.settings.portfolioCodeSelectionForFlowCommitmentForReverseAuction =
            'NOT_APPLICABLE';
    }
}

angular.module('app').component('pkpOperatorSettingsAuctions', {
    controller: OperatorSettingsAuctionsCtrl,
    template:
        require('app/operator-settings/operator-settings-auctions.component.html'),
    bindings: {
        enabledSettings: '<',
        balancingGroupPortfolioCodeSelection: '<'
    }
});
